import { supabase } from '../lib/supabase';

/**
 * Milestone Tracking Service
 * Tracks client completion of onboarding milestones and triggers automated emails
 */

const MILESTONE_TYPES = {
  ORGANIZATION_PROFILE: 'organization_profile',
  EMPLOYEE_IMPORT: 'employee_import',
  COMPLIANCE_SETUP: 'compliance_setup',
  TEAM_ACCESS: 'team_access'
};

/**
 * Get all milestones for a company
 */
export const getCompanyMilestones = async (companyId) => {
  try {
    const { data, error } = await supabase?.from('onboarding_milestones')?.select('*')?.eq('company_id', companyId)?.order('milestone_type');

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching company milestones:', error);
    return { data: null, error: error?.message || 'Failed to fetch milestones' };
  }
};

/**
 * Update milestone progress
 */
export const updateMilestoneProgress = async (companyId, milestoneType, percentage, metadata = {}) => {
  try {
    const { data, error } = await supabase?.rpc('update_milestone_progress', {
      p_company_id: companyId,
      p_milestone_type: milestoneType,
      p_percentage: percentage,
      p_metadata: metadata
    });

    if (error) throw error;
    return { success: true, error: null };
  } catch (error) {
    console.error('Error updating milestone progress:', error);
    return { success: false, error: error?.message || 'Failed to update milestone' };
  }
};

/**
 * Mark milestone as completed
 */
export const completeMilestone = async (companyId, milestoneType, metadata = {}) => {
  return updateMilestoneProgress(companyId, milestoneType, 100, metadata);
};

/**
 * Send milestone completion email
 */
export const sendMilestoneEmail = async (milestoneType, companyName, recipientEmail, nextSteps = null) => {
  try {
    const { data: { session } } = await supabase?.auth?.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    const response = await supabase?.functions?.invoke('send-milestone-email', {
      body: {
        milestoneType,
        companyName,
        recipientEmail,
        nextSteps
      }
    });

    if (response?.error) throw response?.error;
    return { success: true, data: response?.data, error: null };
  } catch (error) {
    console.error('Error sending milestone email:', error);
    return { success: false, data: null, error: error?.message || 'Failed to send email' };
  }
};

/**
 * Get milestone email log
 */
export const getMilestoneEmailLog = async (companyId) => {
  try {
    const { data, error } = await supabase?.from('milestone_email_log')?.select('*')?.eq('company_id', companyId)?.order('created_at', { ascending: false });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching email log:', error);
    return { data: null, error: error?.message || 'Failed to fetch email log' };
  }
};

/**
 * Calculate overall onboarding progress
 */
export const calculateOnboardingProgress = (milestones) => {
  if (!milestones || milestones?.length === 0) return 0;
  
  const totalPercentage = milestones?.reduce((sum, milestone) => {
    return sum + (milestone?.completion_percentage || 0);
  }, 0);
  
  return Math.round(totalPercentage / milestones?.length);
};

/**
 * Get next milestone to complete
 */
export const getNextMilestone = (milestones) => {
  if (!milestones || milestones?.length === 0) return null;
  
  const milestoneOrder = [
    MILESTONE_TYPES?.ORGANIZATION_PROFILE,
    MILESTONE_TYPES?.EMPLOYEE_IMPORT,
    MILESTONE_TYPES?.COMPLIANCE_SETUP,
    MILESTONE_TYPES?.TEAM_ACCESS
  ];
  
  for (const type of milestoneOrder) {
    const milestone = milestones?.find(m => m?.milestone_type === type);
    if (milestone && milestone?.milestone_status !== 'completed') {
      return milestone;
    }
  }
  
  return null;
};

export default {
  MILESTONE_TYPES,
  getCompanyMilestones,
  updateMilestoneProgress,
  completeMilestone,
  sendMilestoneEmail,
  getMilestoneEmailLog,
  calculateOnboardingProgress,
  getNextMilestone
};